import { useState, useMemo } from "react";
import {
  Box, Typography, IconButton, Tooltip, Menu, MenuItem, Chip, Divider, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { formatDistanceToNow } from "date-fns";
import AvatarStack from "./AvatarStack";

/**
 * Compact project page header: name + supporting metadata + actions.
 *
 *   📂  <Project name>                           [edit] [share] [⋯]
 *       Owned by X · ●avatars · published Yh ago · ● Active
 *
 * Replaces the old big DeploymentPanel card. Technical details live behind
 * the [⋯] menu (opens a side drawer).
 *
 * Props:
 *   project: { name, description, is_owned_by_me, ... }
 *   deploymentInfo: response from GET /projects/{id}/deployment, or null
 *     - deployment, effective_run_id, owner_email, owner_display_name, is_owner
 *   onEdit, onShare, onViewDetails   handlers
 *   currentUserCanEdit: bool
 *   isAdmin: bool
 */
export default function ProjectHeaderStrip({
  project,
  deploymentInfo,
  onEdit,
  onShare,
  onViewDetails,                 // null = hide the menu item (caller-gated; server is authoritative)
  onDelete,                      // called after the user confirms; receives no args
  onDeploy,                      // when provided AND canDeploy, renders the Deploy CTA
  canDeploy = false,
  deployLabel = "Deploy",        // "Deploy" before, "Update deployment" after a composition exists
  currentUserCanEdit,
  canSeeDetails = true,
}) {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const dep = deploymentInfo?.deployment || null;
  const ownerLabel = deploymentInfo?.owner_display_name
    || deploymentInfo?.owner_email
    || deploymentInfo?.owner_user_id
    || "—";
  const isOwner = !!deploymentInfo?.is_owner;

  // Build avatar list: owner first, then each shared instance
  const accessUsers = useMemo(() => {
    const list = [];
    if (deploymentInfo?.owner_user_id) {
      list.push({
        user_id: deploymentInfo.owner_user_id,
        email: deploymentInfo.owner_email,
        display_name: deploymentInfo.owner_display_name,
        is_owner: true,
      });
    }
    for (const inst of dep?.shared_instances || []) {
      list.push({
        user_id: inst.user_id,
        email: inst.email,
        display_name: inst.display_name,
        is_admin: inst.is_admin,
      });
    }
    return list;
  }, [deploymentInfo, dep]);

  const publishedAgo = useMemo(() => {
    if (!dep?.created_at) return null;
    try {
      return formatDistanceToNow(new Date(dep.created_at), { addSuffix: true });
    } catch {
      return null;
    }
  }, [dep]);

  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Line 1: name + actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <FolderOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
        <Typography variant="h6" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
          {project.name}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {canDeploy && onDeploy && (
          <Button
            size="small"
            variant="contained"
            startIcon={<RocketLaunchIcon fontSize="small" />}
            onClick={onDeploy}
            sx={{ textTransform: "none", boxShadow: "none", mr: 0.5 }}
          >
            {deployLabel}
          </Button>
        )}
        {currentUserCanEdit && (
          <Tooltip title="Edit project">
            <IconButton size="small" onClick={onEdit} sx={{ color: "text.secondary" }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {currentUserCanEdit && (
          <Tooltip title="Share">
            <IconButton size="small" onClick={onShare} sx={{ color: "text.secondary" }}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="More">
          <IconButton size="small" onClick={handleMenuOpen} sx={{ color: "text.secondary" }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Line 2: meta strip */}
      {dep && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, ml: 4, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            Owned by{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {ownerLabel}
            </Box>
            {isOwner && (
              <Chip label="you" size="small" variant="outlined"
                    sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
            )}
          </Typography>

          {accessUsers.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
              <Tooltip title={`${accessUsers.length} ${accessUsers.length === 1 ? "person has" : "people have"} access`}>
                <Box onClick={onShare} sx={{ cursor: "pointer" }}>
                  <AvatarStack users={accessUsers} max={5} size={22} />
                </Box>
              </Tooltip>
            </>
          )}

          {publishedAgo && (
            <>
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
              <Tooltip title={new Date(dep.created_at).toLocaleString()}>
                <Typography variant="caption" color="text.secondary">
                  Published {publishedAgo}
                </Typography>
              </Tooltip>
            </>
          )}

          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%",
              bgcolor: dep.status === "active" ? "success.main" : "text.disabled",
            }} />
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
              {dep.status || "unknown"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Description (optional) */}
      {project.description && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.75 }}>
          {project.description}
        </Typography>
      )}

      {/* Actions menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
        {canSeeDetails && onViewDetails && (
          <MenuItem onClick={() => { handleMenuClose(); onViewDetails(); }}>
            View deployment details
          </MenuItem>
        )}
        {currentUserCanEdit && (
          <MenuItem onClick={() => { handleMenuClose(); onShare?.(); }}>
            Manage sharing
          </MenuItem>
        )}
        {currentUserCanEdit && onDelete && (
          <MenuItem
            onClick={() => { handleMenuClose(); setDeleteText(""); setDeleteOpen(true); }}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Delete project
          </MenuItem>
        )}
      </Menu>

      {/* Delete confirm — type-the-name pattern. Soft delete server-side. */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will hide the project from your list. Deployments, shared
            instances, and chat threads are preserved and can be restored by
            an admin if needed.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <Box component="span" sx={{ fontWeight: 700 }}>{project.name}</Box> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            placeholder={project.name}
            autoFocus
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteText !== project.name || deleting}
            onClick={async () => {
              setDeleting(true);
              try { await onDelete(); }
              finally { setDeleting(false); setDeleteOpen(false); }
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
